import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BookOpen, ChevronDown, ChevronRight, Pencil, Plus, Search, FileText, Video, FileImage, AlignLeft, Trash2 } from 'lucide-react';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { toast } from 'sonner';
import { successToast } from '../../lib/successToast';
import { HomeUniLoader } from '../../components/HomeUniLoader';

const LESSON_TYPES = ['video', 'pdf', 'article', 'text', 'quiz'];
const TYPE_ICONS = { video: Video, pdf: FileImage, article: FileText, text: AlignLeft, quiz: FileText };
const TYPE_COLORS = {
  video: 'bg-blue-50 text-blue-700',
  pdf: 'bg-amber-50 text-amber-700',
  article: 'bg-emerald-50 text-emerald-700',
  text: 'bg-slate-50 text-slate-600',
  quiz: 'bg-purple-50 text-purple-700',
};

const BLANK_LESSON = { title: '', lesson_type: 'video', content_url: '', content_text: '', order: 1, is_published: false };
const BLANK_COURSE_EDIT = { title: '', code: '', description: '', credit_hours: '', status: 'active' };
const BLANK_NEW_COURSE = { title: '', code: '', description: '', credit_hours: 3, status: 'active', programme_id: '' };

export default function CoursesManagement() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [lessons, setLessons] = useState({});
  const [loadingLessons, setLoadingLessons] = useState({});

  const [editCourse, setEditCourse] = useState(null);
  const [editCourseForm, setEditCourseForm] = useState(BLANK_COURSE_EDIT);
  const [programmes, setProgrammes] = useState([]);
  const [newCourseDialog, setNewCourseDialog] = useState(false);
  const [newCourseForm, setNewCourseForm] = useState(BLANK_NEW_COURSE);
  const [confirm, setConfirm] = useState({ open: false, title: '', description: '', onConfirm: null });

  const [lessonDialog, setLessonDialog] = useState(null); // { courseId, lesson? }
  const [lessonForm, setLessonForm] = useState(BLANK_LESSON);

  const fetchCourses = useCallback(async () => {
    try {
      const [cRes, pRes] = await Promise.all([
        api.get('/academic/courses'),
        api.get('/academic/programmes'),
      ]);
      setCourses(Array.isArray(cRes.data) ? cRes.data : []);
      setProgrammes(Array.isArray(pRes.data) ? pRes.data : []);
    } catch { toast.error('Failed to load courses'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const fetchLessons = async (courseId) => {
    if (lessons[courseId]) return;
    setLoadingLessons(l => ({ ...l, [courseId]: true }));
    try {
      const r = await api.get(`/lms/lessons?course_id=${courseId}`);
      const data = Array.isArray(r.data) ? r.data : (r.data?.lessons || []);
      setLessons(l => ({ ...l, [courseId]: data.sort((a, b) => (a.order || 0) - (b.order || 0)) }));
    } catch { toast.error('Failed to load lessons'); }
    setLoadingLessons(l => ({ ...l, [courseId]: false }));
  };

  const toggleExpand = (courseId) => {
    if (expandedCourse === courseId) {
      setExpandedCourse(null);
    } else {
      setExpandedCourse(courseId);
      fetchLessons(courseId);
    }
  };

  const handleEditCourse = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/academic/courses/${editCourse.course_id}`, editCourseForm);
      successToast('Course updated');
      setEditCourse(null);
      fetchCourses();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update course'); }
  };

  const handleSaveLesson = async (e) => {
    e.preventDefault();
    const { courseId, lesson } = lessonDialog;
    try {
      if (lesson) {
        await api.put(`/lms/lessons/${lesson.lesson_id}`, { ...lessonForm, course_id: courseId });
        successToast('Lesson updated');
      } else {
        await api.post('/lms/lessons', { ...lessonForm, course_id: courseId });
        successToast('Lesson created');
      }
      setLessonDialog(null);
      setLessonForm(BLANK_LESSON);
      setLessons(l => ({ ...l, [courseId]: undefined }));
      fetchLessons(courseId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save lesson'); }
  };

  const openEditLesson = (courseId, lesson) => {
    setLessonForm({
      title: lesson.title || '',
      lesson_type: lesson.lesson_type || lesson.type || 'video',
      content_url: lesson.content_url || lesson.video_url || '',
      content_text: lesson.content_text || lesson.content || '',
      order: lesson.order || 1,
      is_published: lesson.is_published ?? lesson.published ?? false,
    });
    setLessonDialog({ courseId, lesson });
  };

  const handleDeleteCourse = (courseId, e) => {
    e?.stopPropagation();
    setConfirm({
      open: true, title: 'Delete course?',
      description: 'This will permanently delete the course and all its lessons. This cannot be undone.',
      onConfirm: async () => {
        try {
          await api.delete(`/academic/courses/${courseId}`);
          successToast('Course deleted');
          setCourses(prev => prev.filter(c => c.course_id !== courseId));
          if (expandedCourse === courseId) setExpandedCourse(null);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete course'); }
      },
    });
  };

  const handleDeleteLesson = (courseId, lessonId, e) => {
    e?.stopPropagation();
    setConfirm({
      open: true, title: 'Delete lesson?',
      description: 'This lesson will be permanently removed.',
      onConfirm: async () => {
        try {
          await api.delete(`/lms/lessons/${lessonId}`);
          successToast('Lesson deleted');
          setLessons(prev => ({ ...prev, [courseId]: (prev[courseId] || []).filter(l => l.lesson_id !== lessonId) }));
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete lesson'); }
      },
    });
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      const prog = programmes.find(p => p.programme_id === newCourseForm.programme_id);
      await api.post('/academic/courses', { ...newCourseForm, programme_name: prog?.name || '' });
      successToast('Course created');
      setNewCourseDialog(false);
      setNewCourseForm(BLANK_NEW_COURSE);
      fetchCourses();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create course'); }
  };

  const filtered = courses.filter(c =>
    c.title?.toLowerCase().includes(search.toLowerCase()) ||
    c.programme_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.code?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <HomeUniLoader />;

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Courses & Lessons</h1>
          <p className="text-sm text-slate-500 mt-0.5">{courses.length} courses across all programmes</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-56">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              placeholder="Search courses..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
          <Button size="sm" className="h-9 bg-[#1565C0] hover:bg-[#0D47A1]" onClick={() => { setNewCourseForm(BLANK_NEW_COURSE); setNewCourseDialog(true); }}>
            <Plus className="w-4 h-4 mr-1" /> New Course
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="text-center py-16 text-slate-400">
            <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No courses found</p>
          </div>
        )}
        {filtered.map(course => {
          const isExpanded = expandedCourse === course.course_id;
          const courseLessons = lessons[course.course_id] || [];
          return (
            <Card key={course.course_id} className="border-slate-100 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3">
                <button
                  onClick={() => toggleExpand(course.course_id)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-800 truncate">{course.title}</p>
                    {course.code && <Badge variant="outline" className="text-[10px] shrink-0">{course.code}</Badge>}
                  </div>
                  <p className="text-xs text-slate-400 truncate">{course.programme_name}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className="text-[10px] text-slate-500">
                    {isExpanded ? `${courseLessons.length} lessons` : 'View lessons'}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => {
                      setEditCourse(course);
                      setEditCourseForm({
                        title: course.title || '',
                        code: course.code || '',
                        description: course.description || '',
                        credit_hours: course.credit_hours || '',
                        status: course.status || 'active',
                      });
                    }}
                  >
                    <Pencil className="w-3.5 h-3.5 text-slate-400" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 hover:text-red-500"
                    onClick={(e) => handleDeleteCourse(course.course_id, e)}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-slate-300 hover:text-red-500" />
                  </Button>
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-slate-50 bg-slate-50/50">
                  <div className="px-4 py-2 flex items-center justify-between">
                    <p className="text-xs font-medium text-slate-500">Lessons</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px] px-2"
                      onClick={() => { setLessonForm({ ...BLANK_LESSON, order: courseLessons.length + 1 }); setLessonDialog({ courseId: course.course_id, lesson: null }); }}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Lesson
                    </Button>
                  </div>
                  {loadingLessons[course.course_id] ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="w-4 h-4 border-2 border-[#1976D2] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : courseLessons.length === 0 ? (
                    <p className="text-center text-xs text-slate-400 py-6">No lessons yet</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-transparent">
                          <TableHead className="text-[10px] py-1.5">#</TableHead>
                          <TableHead className="text-[10px] py-1.5">Title</TableHead>
                          <TableHead className="text-[10px] py-1.5">Type</TableHead>
                          <TableHead className="text-[10px] py-1.5">Status</TableHead>
                          <TableHead className="text-[10px] py-1.5 w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {courseLessons.map((lesson, idx) => {
                          const TypeIcon = TYPE_ICONS[lesson.lesson_type || lesson.type] || FileText;
                          return (
                            <TableRow key={lesson.lesson_id} className="bg-white">
                              <TableCell className="py-2 text-xs text-slate-400">{lesson.order ?? idx + 1}</TableCell>
                              <TableCell className="py-2 text-sm font-medium">{lesson.title}</TableCell>
                              <TableCell className="py-2">
                                <Badge className={`text-[10px] ${TYPE_COLORS[lesson.lesson_type || lesson.type] || 'bg-slate-50 text-slate-600'}`}>
                                  <TypeIcon className="w-2.5 h-2.5 mr-1 inline" />
                                  {lesson.lesson_type || lesson.type || 'text'}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-2">
                                <Badge className={(lesson.is_published || lesson.published) ? 'bg-green-50 text-green-700 text-[10px]' : 'bg-slate-100 text-slate-500 text-[10px]'}>
                                  {(lesson.is_published || lesson.published) ? 'Published' : 'Draft'}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-2">
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={() => openEditLesson(course.course_id, lesson)}
                                  >
                                    <Pencil className="w-3 h-3 text-slate-400" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 hover:text-red-500"
                                    onClick={(e) => handleDeleteLesson(course.course_id, lesson.lesson_id, e)}
                                  >
                                    <Trash2 className="w-3 h-3 text-slate-300 hover:text-red-500" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Edit Course Dialog */}
      <Dialog open={!!editCourse} onOpenChange={() => setEditCourse(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Course</DialogTitle></DialogHeader>
          {editCourse && (
            <form onSubmit={handleEditCourse} className="space-y-4">
              <p className="text-xs text-slate-400">Programme: {editCourse.programme_name}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Title</Label>
                  <Input value={editCourseForm.title} onChange={e => setEditCourseForm({ ...editCourseForm, title: e.target.value })} required />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Course Code</Label>
                  <Input value={editCourseForm.code} onChange={e => setEditCourseForm({ ...editCourseForm, code: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Credit Hours</Label>
                  <Input type="number" min="1" max="12" value={editCourseForm.credit_hours} onChange={e => setEditCourseForm({ ...editCourseForm, credit_hours: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description</Label>
                <Textarea value={editCourseForm.description} onChange={e => setEditCourseForm({ ...editCourseForm, description: e.target.value })} rows={3} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Status</Label>
                <Select value={editCourseForm.status} onValueChange={v => setEditCourseForm({ ...editCourseForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setEditCourse(null)}>Cancel</Button>
                <Button type="submit" className="bg-[#1565C0] hover:bg-[#0D47A1]">Save Changes</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={open => setConfirm(s => ({ ...s, open }))}
        title={confirm.title}
        description={confirm.description}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirm.onConfirm || (() => {})}
      />

      {/* New Course Dialog */}
      <Dialog open={newCourseDialog} onOpenChange={setNewCourseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>New Course</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateCourse} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Programme</Label>
              <Select value={newCourseForm.programme_id} onValueChange={v => setNewCourseForm({ ...newCourseForm, programme_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select programme" /></SelectTrigger>
                <SelectContent>{programmes.map(p => <SelectItem key={p.programme_id} value={p.programme_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Title</Label>
                <Input value={newCourseForm.title} onChange={e => setNewCourseForm({ ...newCourseForm, title: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Course Code</Label>
                <Input value={newCourseForm.code} onChange={e => setNewCourseForm({ ...newCourseForm, code: e.target.value })} placeholder="e.g. BBA101" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Credit Hours</Label>
                <Input type="number" min="1" max="12" value={newCourseForm.credit_hours} onChange={e => setNewCourseForm({ ...newCourseForm, credit_hours: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea value={newCourseForm.description} onChange={e => setNewCourseForm({ ...newCourseForm, description: e.target.value })} rows={3} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setNewCourseDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1565C0] hover:bg-[#0D47A1]">Create Course</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Lesson Dialog */}
      <Dialog open={!!lessonDialog} onOpenChange={() => { setLessonDialog(null); setLessonForm(BLANK_LESSON); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{lessonDialog?.lesson ? 'Edit Lesson' : 'Add Lesson'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveLesson} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input value={lessonForm.title} onChange={e => setLessonForm({ ...lessonForm, title: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Lesson Type</Label>
                <Select value={lessonForm.lesson_type} onValueChange={v => setLessonForm({ ...lessonForm, lesson_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LESSON_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Order</Label>
                <Input type="number" min="1" value={lessonForm.order} onChange={e => setLessonForm({ ...lessonForm, order: parseInt(e.target.value) || 1 })} />
              </div>
            </div>
            {['video', 'pdf', 'article'].includes(lessonForm.lesson_type) && (
              <div className="space-y-1.5">
                <Label className="text-xs">Content URL ({lessonForm.lesson_type === 'video' ? 'video link' : 'file link'})</Label>
                <Input
                  value={lessonForm.content_url}
                  onChange={e => setLessonForm({ ...lessonForm, content_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            )}
            {['text', 'article'].includes(lessonForm.lesson_type) && (
              <div className="space-y-1.5">
                <Label className="text-xs">Content Text</Label>
                <Textarea
                  value={lessonForm.content_text}
                  onChange={e => setLessonForm({ ...lessonForm, content_text: e.target.value })}
                  rows={5}
                  placeholder="Lesson content..."
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setLessonForm(f => ({ ...f, is_published: !f.is_published }))}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${lessonForm.is_published ? 'bg-[#1976D2]' : 'bg-slate-200'}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${lessonForm.is_published ? 'translate-x-4' : 'translate-x-1'}`} />
              </button>
              <Label className="text-xs text-slate-600">Published</Label>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => { setLessonDialog(null); setLessonForm(BLANK_LESSON); }}>Cancel</Button>
              <Button type="submit" className="bg-[#1565C0] hover:bg-[#0D47A1]">
                {lessonDialog?.lesson ? 'Save Changes' : 'Add Lesson'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
